import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextFieldProps,
  Typography,
  useTheme,
} from '@mui/material';
import { CheckBox as CheckBoxIcon } from '@mui/icons-material';

import {
  getCourseTemplatesAsAdmin,
  getCourseTemplatesState,
  ICourseTemplateForAdmin,
  loadMoreCourseTemplatesAsAdmin,
  resetAdminCourseTemplatesData,
} from '../../../../../store/V2/CourseTemplatesSlice';
import { useAppSelector } from '../../../../../hooks';
import { TPTextField } from '../../../../../components/Inputs';
import BasicLoader from '../../../../../components/Loaders/BasicLoader';
import {
  createCourseByTemplateAsAdmin,
  getCoursesState,
  resetRedirectUrl,
} from '../../../../../store/V2/CoursesSlice';

interface ViewCourseTemplatesModalProps {
  open: boolean;
  onClose: () => void;
}

type TFilterData = {
  limit: number;
  offset: number;
};

type TState = {
  filterData: TFilterData;
  searchText: string;
  selectedTemplate?: ICourseTemplateForAdmin;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialFilterData: TFilterData = {
  limit: 30,
  offset: 0,
};

const initialState: TState = {
  filterData: { ...initialFilterData },
  searchText: '',
  selectedTemplate: undefined,
};

const ViewCourseTemplatesModal: React.FC<ViewCourseTemplatesModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const courseTemplatesState = useAppSelector(getCourseTemplatesState);

  // State
  const [state, setState] = useState({ ...initialState });

  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(
      getCourseTemplatesAsAdmin({
        ...state.filterData,
        searchText: state.searchText,
      })
    );

    return () => {
      dispatch(resetRedirectUrl());
      dispatch(resetAdminCourseTemplatesData());
    };
  }, []);

  useEffect(() => {
    if (coursesState.redirectUrl) {
      navigate(coursesState.redirectUrl, { replace: true });
    }
  }, [coursesState.redirectUrl]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;

    const newFilterData = {
      ...state.filterData,
      ...initialFilterData,
    };

    setState((prevState) => ({
      ...prevState,
      searchText: value,
      filterData: newFilterData,
    }));

    dispatch(
      getCourseTemplatesAsAdmin({
        ...newFilterData,
        searchText: value.trim(),
      })
    );
  };

  const handleLoadMore = () => {
    if (courseTemplatesState.adminCourseTemplatesData) {
      const newFilterData: TFilterData = {
        ...state.filterData,
        offset: (courseTemplatesState.adminCourseTemplatesData.list || [])
          .length,
      };

      dispatch(
        loadMoreCourseTemplatesAsAdmin({
          ...newFilterData,
          searchText: state.searchText,
        })
      );

      setState((prevState) => ({
        ...prevState,
        filterData: newFilterData,
      }));
    }
  };

  const handleTemplateSelect = (courseTemplate: ICourseTemplateForAdmin) => {
    setState((prevState) => ({
      ...prevState,
      selectedTemplate: courseTemplate,
    }));
  };

  const toggleLoading = () => {
    setLoading((prevState) => !prevState);
  };

  const handleSubmit = async () => {
    toggleLoading();

    if (state.selectedTemplate) {
      await dispatch(
        createCourseByTemplateAsAdmin({
          title: state.selectedTemplate.title,
          templateId: state.selectedTemplate._id,
        })
      );
    }

    toggleLoading();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      // PaperProps={{ sx: { minWidth: '600px', maxWidth: '600px' } }}
    >
      <DialogTitle>Select a Template</DialogTitle>
      <DialogContent sx={{ pt: `${theme.spacing(2)} !important` }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TPTextField
                {...textFieldProps}
                fullWidth
                name="searchText"
                label="Search"
                value={state.searchText}
                onChange={handleSearch}
                placeholder="Search by template name"
              />
            </FormControl>
          </Grid>
          {courseTemplatesState.adminCourseTemplatesData &&
          courseTemplatesState.adminCourseTemplatesData.list &&
          courseTemplatesState.adminCourseTemplatesData.list.length ? (
            <Grid item xs={12}>
              <List disablePadding>
                {courseTemplatesState.adminCourseTemplatesData.list.map(
                  (item, index) => {
                    const isSelected =
                      state.selectedTemplate &&
                      state.selectedTemplate._id === item._id;

                    return [
                      <ListItem key={`li-${index}`} disablePadding>
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => handleTemplateSelect(item)}
                        >
                          <ListItemText
                            title={item.name}
                            className="ellipsis-text one-line"
                            primary={item.name}
                          />
                          {isSelected ? <CheckBoxIcon color="primary" /> : null}
                        </ListItemButton>
                      </ListItem>,
                      <Divider key={`li-divider-${index}`} />,
                    ];
                  }
                )}
              </List>
            </Grid>
          ) : null}
          {courseTemplatesState.status !== 'loading' &&
          courseTemplatesState.adminCourseTemplatesData &&
          !courseTemplatesState.adminCourseTemplatesData.totalDocs ? (
            <Grid item xs={12}>
              <Typography variant="h6">
                No course templates are available.
              </Typography>
            </Grid>
          ) : null}
          {courseTemplatesState.status !== 'loading' &&
          courseTemplatesState.adminCourseTemplatesData &&
          courseTemplatesState.adminCourseTemplatesData.totalDocs &&
          courseTemplatesState.adminCourseTemplatesData.totalDocs >
            courseTemplatesState.adminCourseTemplatesData.list.length ? (
            <Grid item xs={12}>
              <Stack alignItems="center">
                <Button onClick={handleLoadMore}>Load More</Button>
              </Stack>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <BasicLoader
              loaderProps={{ size: 20 }}
              containerProps={{
                visibility:
                  courseTemplatesState.status === 'loading'
                    ? undefined
                    : 'hidden',
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px' }}>
        <LoadingButton
          size="small"
          variant="contained"
          color="primary"
          disabled={!state.selectedTemplate}
          onClick={handleSubmit}
          loading={isLoading}
        >
          Create Course
        </LoadingButton>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewCourseTemplatesModal;
